import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Badge, Button, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { sickLeaveApplicationsApiService } from '../../services/apiServices';
import { SickLeaveApplicationResponse } from '../../api-client';
import {
  getSickLeaveApplicationStatusLabel,
  getSickLeaveApplicationStatusBadgeVariant,
} from '../utils/statusConverters';

const MySickLeaveDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<SickLeaveApplicationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showExtendForm, setShowExtendForm] = useState(false);
  const [newEndDate, setNewEndDate] = useState('');
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [extendSuccess, setExtendSuccess] = useState(false);

  useEffect(() => {
    if (!applicationId) {
      navigate('/sick-leave/my');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await sickLeaveApplicationsApiService.getMyApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const handleExtend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicationId || !newEndDate) return;

    try {
      setExtending(true);
      setExtendError(null);
      const response = await sickLeaveApplicationsApiService.extendMyApplication(
        applicationId,
        newEndDate
      );
      setApplication(response.data || null);
      setShowExtendForm(false);
      setExtendSuccess(true);
      setNewEndDate('');
      setTimeout(() => setExtendSuccess(false), 3000);
    } catch (err: any) {
      setExtendError(err.message || 'Ошибка при продлении больничного');
    } finally {
      setExtending(false);
    }
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/sick-leave/my')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Больничный</h2>
            <Link to="/sick-leave/my">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {extendSuccess && <Alert variant="success">Больничный успешно продлен!</Alert>}
          {extendError && <Alert variant="warning">{extendError}</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={getSickLeaveApplicationStatusBadgeVariant(application.status)}>
                {getSickLeaveApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>ID заявки</th>
                    <td>{application.id}</td>
                  </tr>
                  <tr>
                    <th>Дата начала</th>
                    <td>{new Date(application.start_date).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <th>Дата окончания</th>
                    <td>{new Date(application.end_date).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <th>Статус</th>
                    <td>
                      <Badge bg={getSickLeaveApplicationStatusBadgeVariant(application.status)}>
                        {getSickLeaveApplicationStatusLabel(application.status)}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Можно продлить</th>
                    <td>{application.is_extensible ? 'Да' : 'Нет'}</td>
                  </tr>
                  <tr>
                    <th>Дата создания</th>
                    <td>{new Date(application.created_at).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>

              {application.is_extensible && (
                <div className="mt-3">
                  {!showExtendForm ? (
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        setShowExtendForm(true);
                        setExtendError(null);
                      }}
                    >
                      Продлить больничный
                    </Button>
                  ) : (
                    <Card>
                      <Card.Header>Продление больничного</Card.Header>
                      <Card.Body>
                        <Form onSubmit={handleExtend}>
                          {extendError && <Alert variant="danger">{extendError}</Alert>}
                          <Form.Group className="mb-3">
                            <Form.Label>Новая дата окончания</Form.Label>
                            <Form.Control
                              type="date"
                              value={newEndDate}
                              onChange={(e) => setNewEndDate(e.target.value)}
                              min={
                                new Date(
                                  Math.max(Date.now(), new Date(application.end_date).getTime())
                                )
                                  .toISOString()
                                  .split('T')[0]
                              }
                              disabled={extending}
                              style={{ width: 'auto' }}
                              required
                            />
                            <Form.Text className="text-muted">
                              Дата должна быть позже текущей даты окончания (
                              {new Date(application.end_date).toLocaleDateString()})
                            </Form.Text>
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              type="submit"
                              disabled={extending || !newEndDate}
                            >
                              {extending ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-1" />
                                  Продление...
                                </>
                              ) : (
                                'Продлить'
                              )}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              onClick={() => {
                                setShowExtendForm(false);
                                setExtendError(null);
                                setNewEndDate('');
                              }}
                              disabled={extending}
                            >
                              Отмена
                            </Button>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MySickLeaveDetailPage;
